const DEMO_USER = {
  id: 'demo-user',
  email: 'camille@demo.com',
  user_metadata: {
    full_name: 'Camille Demo',
  },
}

const DEMO_SESSION = {
  access_token: 'demo-access-token',
  refresh_token: 'demo-refresh-token',
  expires_in: 3600,
  token_type: 'bearer',
  user: DEMO_USER,
}

const TABLE_KEYS = {
  profiles: 'medtrack-demo-profiles',
  health_entries: 'medtrack-demo-health-entries',
  doctor_patient_access: 'medtrack-demo-doctor-patient-access',
} as const

type Filter =
  | { type: 'eq'; field: string; value: unknown }
  | { type: 'in'; field: string; value: unknown[] }

let activeSession: typeof DEMO_SESSION | null = DEMO_SESSION
const listeners = new Set<(event: string, session: typeof DEMO_SESSION | null) => void>()

const timestamp = () => new Date().toISOString()

const readTable = <T,>(key: string, fallback: T[]) => {
  const stored = localStorage.getItem(key)

  if (!stored) {
    localStorage.setItem(key, JSON.stringify(fallback))
    return fallback
  }

  return JSON.parse(stored) as T[]
}

const writeTable = (key: string, value: unknown) => {
  localStorage.setItem(key, JSON.stringify(value))
}

const ensureSeedData = () => {
  const profiles = readTable(TABLE_KEYS.profiles, [
    {
      id: DEMO_USER.id,
      email: DEMO_USER.email,
      full_name: 'Camille Demo',
      role: 'patient',
      created_at: timestamp(),
      updated_at: timestamp(),
    },
  ])

  if (profiles.length === 0) {
    writeTable(TABLE_KEYS.profiles, [
      {
        id: DEMO_USER.id,
        email: DEMO_USER.email,
        full_name: 'Camille Demo',
        role: 'patient',
        created_at: timestamp(),
        updated_at: timestamp(),
      },
    ])
  }

  readTable(TABLE_KEYS.health_entries, [])
  readTable(TABLE_KEYS.doctor_patient_access, [])
}

const emit = (event: string) => {
  listeners.forEach((listener) => listener(event, activeSession))
}

const matchesFilters = (row: Record<string, unknown>, filters: Filter[]) =>
  filters.every((filter) => {
    if (filter.type === 'eq') {
      return row[filter.field] === filter.value
    }

    return filter.value.includes(row[filter.field])
  })

const getTableKey = (table: string) => TABLE_KEYS[table as keyof typeof TABLE_KEYS] ?? table

const buildResult = (rows: Record<string, unknown>[], mode: 'many' | 'single' | 'maybe') => {
  if (mode === 'many') {
    return { data: rows, error: null }
  }

  if (rows.length === 0) {
    return { data: null, error: null }
  }

  return { data: rows[0], error: null }
}

const createQueryBuilder = (table: string) => {
  ensureSeedData()

  const state = {
    action: 'select' as 'select' | 'upsert' | 'insert',
    payload: [] as Record<string, unknown>[],
    filters: [] as Filter[],
    orderField: null as string | null,
    ascending: true,
    limitCount: null as number | null,
    mode: 'many' as 'many' | 'single' | 'maybe',
  }

  const builder = {
    select() {
      state.action = 'select'
      return builder
    },
    upsert(payload: Record<string, unknown> | Record<string, unknown>[]) {
      state.action = 'upsert'
      state.payload = Array.isArray(payload) ? payload : [payload]
      return builder
    },
    insert(payload: Record<string, unknown> | Record<string, unknown>[]) {
      state.action = 'insert'
      state.payload = Array.isArray(payload) ? payload : [payload]
      return builder
    },
    eq(field: string, value: unknown) {
      state.filters.push({ type: 'eq', field, value })
      return builder
    },
    in(field: string, value: unknown[]) {
      state.filters.push({ type: 'in', field, value })
      return builder
    },
    order(field: string, options?: { ascending?: boolean }) {
      state.orderField = field
      state.ascending = options?.ascending ?? true
      return builder
    },
    limit(count: number) {
      state.limitCount = count
      return builder
    },
    single() {
      state.mode = 'single'
      return builder
    },
    maybeSingle() {
      state.mode = 'maybe'
      return builder
    },
    async execute() {
      const key = getTableKey(table)
      const rows = readTable<Record<string, unknown>>(key, [])

      if (state.action === 'select') {
        let result = rows.filter((row) => matchesFilters(row, state.filters))

        if (state.orderField) {
          result = [...result].sort((left, right) => {
            const leftValue = left[state.orderField as string]
            const rightValue = right[state.orderField as string]

            if (leftValue === rightValue) {
              return 0
            }

            if (leftValue === undefined || leftValue === null) {
              return state.ascending ? 1 : -1
            }

            if (rightValue === undefined || rightValue === null) {
              return state.ascending ? -1 : 1
            }

            return (leftValue > rightValue ? 1 : -1) * (state.ascending ? 1 : -1)
          })
        }

        if (state.limitCount !== null) {
          result = result.slice(0, state.limitCount)
        }

        return buildResult(result, state.mode)
      }

      const nextRows = [...rows]
      const touchedRows: Record<string, unknown>[] = []

      state.payload.forEach((item) => {
        const now = timestamp()

        if (table === 'profiles') {
          const index = nextRows.findIndex((row) => row.id === item.id)
          const nextValue = {
            created_at: now,
            updated_at: now,
            role: 'patient',
            ...item,
          }

          if (index >= 0) {
            nextRows[index] = {
              ...nextRows[index],
              ...nextValue,
            }
            touchedRows.push(nextRows[index])
          } else {
            nextRows.push(nextValue)
            touchedRows.push(nextValue)
          }

          return
        }

        if (table === 'health_entries') {
          const rowId = (item.id as string | undefined) ?? crypto.randomUUID()
          const ownerId = item.owner_id as string
          const entryDate = item.entry_date as string
          const index = nextRows.findIndex(
            (row) => row.owner_id === ownerId && row.entry_date === entryDate,
          )

          const nextValue = {
            id: rowId,
            created_at: now,
            updated_at: now,
            bleeding_present: false,
            urgent_alert: false,
            symptoms: [],
            triggers: [],
            medications_taken: [],
            metadata: {},
            ...item,
          }

          if (index >= 0) {
            nextRows[index] = {
              ...nextRows[index],
              ...nextValue,
              id: nextRows[index].id,
              created_at: nextRows[index].created_at,
            }
            touchedRows.push(nextRows[index])
          } else {
            nextRows.push(nextValue)
            touchedRows.push(nextValue)
          }

          return
        }

        nextRows.push(item)
        touchedRows.push(item)
      })

      writeTable(key, nextRows)
      return buildResult(touchedRows, state.mode)
    },
    then(
      onfulfilled?: ((value: { data: unknown; error: null }) => unknown) | null,
      onrejected?: ((reason: unknown) => unknown) | null,
    ) {
      return builder.execute().then(onfulfilled, onrejected)
    },
    catch(onrejected?: ((reason: unknown) => unknown) | null) {
      return builder.execute().catch(onrejected)
    },
    finally(onfinally?: (() => void) | null) {
      return builder.execute().finally(onfinally)
    },
  }

  return builder
}

export const createDemoClient = () => {
  ensureSeedData()
  console.log('🔧 Mode démo activé - Les données sont stockées en local dans le navigateur.')

  return {
    auth: {
      signInWithPassword: async ({ email }: { email: string }) => {
        activeSession = {
          ...DEMO_SESSION,
          user: {
            ...DEMO_USER,
            email,
          },
        }
        emit('SIGNED_IN')
        return { data: { user: activeSession.user, session: activeSession }, error: null }
      },
      signUp: async ({
        email,
        options,
      }: {
        email: string
        options?: { data?: { full_name?: string } }
      }) => {
        activeSession = {
          ...DEMO_SESSION,
          user: {
            ...DEMO_USER,
            email,
            user_metadata: {
              full_name: options?.data?.full_name ?? 'Camille Demo',
            },
          },
        }

        const profiles = readTable<Record<string, unknown>>(TABLE_KEYS.profiles, [])
        const alreadyExists = profiles.some((profile) => profile.id === DEMO_USER.id)

        if (!alreadyExists) {
          profiles.push({
            id: DEMO_USER.id,
            email,
            full_name: options?.data?.full_name ?? 'Camille Demo',
            role: 'patient',
            created_at: timestamp(),
            updated_at: timestamp(),
          })
          writeTable(TABLE_KEYS.profiles, profiles)
        }

        emit('SIGNED_IN')
        return { data: { user: activeSession.user, session: activeSession }, error: null }
      },
      signOut: async () => {
        activeSession = null
        emit('SIGNED_OUT')
        return { error: null }
      },
      getSession: async () => ({ data: { session: activeSession }, error: null }),
      getUser: async () => ({ data: { user: activeSession?.user ?? null }, error: null }),
      onAuthStateChange: (callback: (event: string, session: typeof DEMO_SESSION | null) => void) => {
        listeners.add(callback)
        callback('INITIAL_SESSION', activeSession)
        return {
          data: {
            subscription: {
              unsubscribe: () => {
                listeners.delete(callback)
              },
            },
          },
        }
      },
    },
    from: (table: string) => createQueryBuilder(table),
  } as any
}
